import {
  Box,
  Container,
  makeStyles,
  Typography,
  Chip,
  Button,
  CircularProgress,
  IconButton,
} from "@material-ui/core";
import {
  InfoBox,
  InfoLine,
} from "../components-molecules";
import { ProOrganisationDetailHeader } from "../components-molecules/ProOrganisationDetailHeader";
import { EditProOrganisationModal, EditProOrganisationData } from "../components-molecules/EditProOrganisationModal";
import { InviteMemberModal, InviteMemberData } from "../components-molecules/InviteMemberModal";
import { EditProUserModal } from "../components-molecules/EditProUserModal";
import { ConfirmModal } from "../components-molecules/ConfirmModal";
import EditIcon from "@material-ui/icons/Edit";
import { ITheme, colours } from "../constants/theme";
import { useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { getProOrganisationDetails, editProOrganisation, inviteNewMemberToProOrganisation, ProOrganisationDetails, ProOrganisationMember, archiveProOrganisation } from "../redux/actions/subscriptions";
import { useParams } from "react-router-dom";
import moment from "moment";
import { getDateTimeFormat } from "../utils/dateTimeFormatting";
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: ITheme) => ({
  container: {
    display: "flex",
    justifyContent: "center",
  },
  root: {
    maxWidth: "840px",
    paddingLeft: 0,
    paddingRight: 0,
  },
  detailsContainer: {
    marginTop: "24px",
    paddingLeft: 0,
    paddingRight: 0,
  },
  membersContainer: {
    marginTop: 16,
  },
  membersSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    marginTop: 24,
    paddingLeft: 0,
    paddingRight: 0,
  },
  membersSectionTitle: {
    fontWeight: 600,
    fontSize: '1.25rem',
    color: theme.palette.text.primary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  inviteButton: {
    fontSize: '0.875rem',
    fontWeight: 600,
    textTransform: 'none',
    padding: '8px 16px',
    borderRadius: 8,
  },
  memberList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 16,
    marginTop: 16,
    paddingBottom: 24,
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },
  memberContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '16px 20px',
    border: `1px solid ${colours.dark12}`,
    borderRadius: 12,
    backgroundColor: '#fff',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  memberInfoContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flex: 1,
  },
  memberNameContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  memberName: {
    fontWeight: 600,
    fontSize: '1rem',
    color: theme.palette.text.primary,
  },
  roleStamp: {
    fontSize: '0.7rem',
    padding: '4px 10px',
    borderRadius: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    lineHeight: 1,
    display: 'inline-block',
  },
  teamManagerStamp: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
  },
  teamMemberStamp: {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.text.primary,
  },
  memberEmail: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginBottom: 4,
  },
  memberDetails: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
  },
  memberStatusContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 8,
  },
  collectEnabled: {
    color: theme.palette.success.main,
    fontWeight: 700,
  },
  collectDisabled: {
    color: theme.palette.error.main,
    fontWeight: 700,
  },
  statusChip: {
    fontSize: '0.7rem',
    height: 24,
    fontWeight: 600,
    letterSpacing: '0.3px',
    textTransform: 'uppercase',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
  }
}));

export const ProOrganisationDetailsPage = () => {
  const params: any = useParams();
  const userData = useSelector((state: any) => state.login);
  const isAdmin = useMemo(() => userData.role === "Admin", [userData]);
  const classes = useStyles();
  const { t } = useTranslation();

  const [organisation, setOrganisation] = useState<ProOrganisationDetails>();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [editUserModalVisible, setEditUserModalVisible] = useState(false);
  const [selectedMember, setSelectedMember] = useState<ProOrganisationMember | null>(null);
  const [archiveModalVisible, setArchiveModalVisible] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);

  function getOrganisationData() {
    setInitialLoading(true);
    getProOrganisationDetails(
      params.id,
      (res) => {
        setOrganisation(res);
        setInitialLoading(false);
      },
      (err) => {
        setInitialLoading(false);
      }
    );
  }

  useEffect(() => {
    getOrganisationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDateTime = (dateString: string) => {
    return moment(dateString).format(getDateTimeFormat());
  };

  const handleEdit = () => {
    setEditModalVisible(true);
  };

  const handleArchive = () => {
    setArchiveModalVisible(true);
  };

  const handleArchiveConfirm = () => {
    if (!organisation?.Id) return;
    
    archiveProOrganisation(
      organisation.Id,
      (response) => {
        setArchiveModalVisible(false);
        window.history.back();
      },
      (error) => {
      }
    );
  };

  const handleArchiveModalClose = () => {
    setArchiveModalVisible(false);
  };

  const handleEditSubmit = (data: EditProOrganisationData) => {
    if (!organisation?.Id) return;
    
    setEditLoading(true);
    editProOrganisation(
      organisation.Id,
      data,
      (response) => {
        setEditLoading(false);
        setEditModalVisible(false);
        // Refresh the organisation data
        getOrganisationData();
      },
      (error) => {
        setEditLoading(false);
        // TODO: Show error message to user
      }
    );
  };

  const handleEditModalClose = () => {
    setEditModalVisible(false);
  };

  const handleInviteMember = () => {
    setInviteModalVisible(true);
  };

  const handleInviteSubmit = (data: InviteMemberData) => {
    if (!organisation?.Id) return;
    
    setInviteLoading(true);
    inviteNewMemberToProOrganisation(
      organisation.Id,
      data,
      (response) => {
        setInviteLoading(false);
        setInviteModalVisible(false);
        // Refresh the organisation data to show the new member
        getOrganisationData();
      },
      (error) => {
        setInviteLoading(false);
        // TODO: Show error message to user
      }
    );
  };

  const handleInviteModalClose = () => {
    setInviteModalVisible(false);
  };

  const handleEditUser = (member: ProOrganisationMember) => {
    setSelectedMember(member);
    setEditUserModalVisible(true);
  };

  const handleEditUserModalClose = () => {
    setEditUserModalVisible(false);
    setSelectedMember(null);
  };

  const handleEditUserSuccess = () => {
    getOrganisationData(); // Refresh the data
  };

  const renderMember = (member: ProOrganisationMember) => {
    const isTeamManager = member.RoleName?.toLowerCase().includes('manager');
    
    return (
      <Box key={member.CustomerId} className={classes.memberContainer}>
        <Box className={classes.memberInfoContainer}>
          <Box className={classes.memberNameContainer}>
            <Typography className={classes.memberName}>
              {member.FirstName} {member.LastName}
            </Typography>
            <Box 
              className={`${classes.roleStamp} ${
                isTeamManager ? classes.teamManagerStamp : classes.teamMemberStamp
              }`}
            >
              {member.RoleName}
            </Box>
          </Box>
          
          <Typography className={classes.memberEmail}>
            {member.Email}
          </Typography>
          
          {member.PhoneNumber && (
            <Typography className={classes.memberDetails}>
              {member.PhoneNumber}
            </Typography>
          )}
          
          {member.ApprovalThreshold && (
            <Typography className={classes.memberDetails}>
              {t('proOrganisationDetails.approvalThreshold')}: {t('currencySymbol')}{(member.ApprovalThreshold / 100).toFixed(2)}
            </Typography>
          )}
        </Box>
        
        <Box className={classes.memberStatusContainer}>
          <IconButton
            size="small"
            onClick={() => handleEditUser(member)}
            style={{ 
              marginRight: 8,
              padding: 4
            }}
          >
            <EditIcon style={{ fontSize: 16, color: '#666' }} />
          </IconButton>
          {member.Accepted ? (
            <Chip
              label={t('proOrganisationDetails.active')}
              color="primary"
              size="small"
              className={classes.statusChip}
            />
          ) : (
            <Chip
              label={t('proOrganisationDetails.pending')}
              color="secondary"
              size="small"
              className={classes.statusChip}
            />
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box className={classes.container}>
      <Container className={classes.root}>
        {initialLoading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {isAdmin &&
              <ProOrganisationDetailHeader
                name={organisation?.Name}
                status={organisation?.IsCollectEnabled ? "TKD Enabled" : "TKD Disabled"}
                ProTeamId={organisation?.Id}
                isAdmin={isAdmin}
                onEdit={handleEdit}
                onArchive={handleArchive}
              />
            }
            <Container className={classes.detailsContainer}>
          <InfoBox title={t('proOrganisationDetails.organisationInformation')}>
            <InfoLine
              name={t('proOrganisationDetails.organisationName')}
              value={organisation?.Name || "-"}
            />
            <InfoLine
              name={t('proOrganisationDetails.phoneNumber')}
              value={organisation?.PhoneNumber || "-"}
            />
            <InfoLine
              name={t('proOrganisationDetails.vatNumber')}
              value={organisation?.VATNumber || "-"}
            />
            <InfoLine
              name={t('proOrganisationDetails.createdDate')}
              value={organisation?.Created ? formatDateTime(organisation.Created) : "-"}
            />
            <InfoLine
              name={t('proOrganisationDetails.tkdEnabled')}
              value={
                <Typography
                  className={organisation?.IsCollectEnabled ? classes.collectEnabled : classes.collectDisabled}
                >
                  {organisation?.IsCollectEnabled ? t('proOrganisationDetails.yes') : t('proOrganisationDetails.no')}
                </Typography>
              }
            />
            <InfoLine
              name={t('proOrganisationDetails.stripeCustomerId')}
              value={organisation?.StripeCustomerId || "-"}
            />
            <InfoLine
              name={t('proOrganisationDetails.stripePaymentIdentifier')}
              value={organisation?.StripePaymentIdentifier || "-"}
            />
          </InfoBox>

          {organisation?.Members && Array.isArray(organisation.Members) && organisation.Members.length > 0 && (
            <Box>
              <Box className={classes.membersSectionHeader}>
                <Typography variant="h6" className={classes.membersSectionTitle}>
                  {t('proOrganisationDetails.members')}
                </Typography>
                {isAdmin && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleInviteMember}
                    className={classes.inviteButton}
                  >
                    {t('proOrganisationDetails.inviteMember')}
                  </Button>
                )}
              </Box>
              <Box className={classes.memberList}>
                {organisation.Members.map(renderMember)}
              </Box>
            </Box>
          )}
        </Container>
          </>
        )}
      </Container>
      
      <EditProOrganisationModal
        visible={editModalVisible}
        onClose={handleEditModalClose}
        onSubmit={handleEditSubmit}
        organisation={organisation}
        loading={editLoading}
      />
      
      <InviteMemberModal
        visible={inviteModalVisible}
        onClose={handleInviteModalClose}
        onSubmit={handleInviteSubmit}
        loading={inviteLoading}
      />
      
      <EditProUserModal
        visible={editUserModalVisible}
        onClose={handleEditUserModalClose}
        onSuccess={handleEditUserSuccess}
        member={selectedMember}
        proTeamId={params.id}
      />
      
      <ConfirmModal
        visible={archiveModalVisible}
        onClose={handleArchiveModalClose}
        onSubmit={handleArchiveConfirm}
        title={t('proOrganisationDetails.archiveTkProTeam')}
        errorBody={t('proOrganisationDetails.archiveTeamConfirmation', { name: organisation?.Name })}
        buttonTitle={t('proOrganisationDetails.yesArchiveTeam')}
        cancelButtonTitle={t('proOrganisationDetails.cancel')}
      />
    </Box>
  );
};
